import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useNews } from '../context/NewsContext';
import '../styles/components/info-page.scss';
import { baseURLnoapi } from '../Constants';

interface InfoPageProps {
  slug?: string;
}

const InfoPage: React.FC<InfoPageProps> = ({ slug }) => {
  const { slug: urlSlug } = useParams<{ slug: string }>();
  const { getArticlesByTag, isLoading } = useNews();
  const [article, setArticle] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  
  // Use prop slug or URL slug
  const pageSlug = slug || urlSlug;
  
  // Map slugs to article titles
  const slugToTitle: { [key: string]: string } = {
    'rules': 'Rules',
    'prizepool': 'Prize Pool',
    'format': 'Format', 
    'schedule': 'Schedule',
    'faq': 'FAQ'
  };
  
  const pageTitle = slugToTitle[pageSlug || ''] || 'Information';

  useEffect(() => {
    const fetchInfoPage = async () => {
      if (!pageSlug) return;
      
      try {
        // Get all articles with "info" category
        const infoArticles = await getArticlesByTag('info');
        
        // Find article with matching title
        const targetTitle = slugToTitle[pageSlug];
        const foundArticle = infoArticles.find(
          article => article.title.toLowerCase() === targetTitle?.toLowerCase()
        );
        
        if (foundArticle) {
          setArticle(foundArticle);
          setError(null);
        } else {
          setError(`${pageTitle} page not found. Please contact an administrator to create this content.`);
        }
      } catch (err) {
        console.error('Error fetching info page:', err);
        setError('Failed to load page content. Please try again later.');
      }
    };

    fetchInfoPage();
    
    // Scroll to top on page load
    window.scrollTo(0, 0);
    
    // Set page title
    document.title = `${pageTitle} | Northern Championships Series`;
  }, [pageSlug, pageTitle, getArticlesByTag]);

  // Helper function to get image URL
  const getImageUrl = (imageUrl: string | null) => {
    if (!imageUrl) return null;
    
    if (imageUrl.startsWith('http')) {
      return imageUrl;
    } else {
      return `${baseURLnoapi}${imageUrl}`;
    }
  };

  if (isLoading) {
    return (
      <div className="info-page">
        <div className="container">
          <div className="loading">
            <div className="loading-spinner"></div>
            <p>Loading {pageTitle.toLowerCase()}...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="info-page">
        <div className="container">
          <div className="error-container">
            <h1>{pageTitle}</h1>
            <div className="error-message">
              <p>{error}</p>
            </div>
            <Link to="/" className="btn btn-primary">
              Return to Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="info-page">
        <div className="container">
          <div className="error-container">
            <h1>{pageTitle}</h1>
            <div className="error-message">
              <p>This page is not available yet. Please check back later.</p>
            </div>
            <Link to="/" className="btn btn-primary">
              Return to Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="info-page">
      {/* Header with optional background image */}
      {article.imageUrl && (
        <div 
          className="info-hero"
          style={{ 
            backgroundImage: `url('${getImageUrl(article.imageUrl)}')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        >
          <div className="hero-overlay"></div>
          <div className="container">
            <div className="hero-content">
              <h1>{article.title}</h1>
            </div>
          </div>
        </div>
      )}
      
      {/* Content section */}
      <div className="info-content">
        <div className="container">
          {/* Page title if no hero image */}
          {!article.imageUrl && (
            <div className="page-header">
              <h1>{article.title}</h1>
            </div>
          )}
          
          {/* Main content */}
          <div className="info-body">
            <article className="content">
              <div 
                className="content-html"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />
            </article>
            
            {/* Last updated info */}
            <div className="page-meta">
              <p className="last-updated">
                Last updated: {new Date(article.date).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InfoPage;